import { Dialog } from '@base-ui/react/dialog';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, PropsWithChildren } from 'react';
import { useOverlayPortalContainer } from '../OverlayPortalContainer/overlayPortalContainer';
import { useIsMdBreakpoint, useModalDrag } from './useModalDrag';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalCoreProps {
  isOpen: boolean;
  onClose?: () => void;
  className?: string;
  disableBackdropClick?: boolean;
  /** Enable desktop-only drag-to-reposition via the header drag handle. */
  draggable?: boolean;
  panelProps?: Dialog.Popup.Props;
  size?: ModalSize;
  /**
   * Overlay behaviour, mapped 1:1 to Base UI `Dialog.Root`'s `modal`:
   * - `'trap-focus'` (default): focus is trapped, but background scroll stays
   *   unlocked and outside content stays interactive.
   * - `true`: background scroll is locked and outside content is inert.
   * - `false`: no focus trap.
   */
  modal?: boolean | 'trap-focus';
  /** Called after the open animation completes. */
  afterEnter?: () => void;
  /** Called after the close animation completes. */
  afterLeave?: () => void;
}

const ModalCore: FC<PropsWithChildren<ModalCoreProps>> = (props) => {
  const {
    className,
    children,
    isOpen,
    onClose,
    disableBackdropClick = true,
    draggable = false,
    panelProps,
    size,
    modal = 'trap-focus',
    afterEnter,
    afterLeave,
  } = props;

  const container = useOverlayPortalContainer();
  const isMdAndUp = useIsMdBreakpoint();
  const drag = useModalDrag({ isOpen, draggable, enabled: isMdAndUp });

  return (
    <Dialog.Root
      open={isOpen}
      modal={modal}
      onOpenChange={(open, details) => {
        if (open) return;
        // Headless UI's single `onClose` governed both outside-press and Escape;
        // Base UI splits them into separate reasons, so gate both.
        if (
          disableBackdropClick &&
          (details.reason === 'outside-press' ||
            details.reason === 'escape-key')
        ) {
          details.cancel();
          return;
        }
        onClose?.();
      }}
      onOpenChangeComplete={(open) => (open ? afterEnter?.() : afterLeave?.())}
    >
      <Dialog.Portal container={container ?? undefined}>
        <Dialog.Backdrop
          // Only the outermost dialog renders a backdrop: Base UI hides nested
          // ones so the stack shows through cleanly. A nested modal dims the one
          // beneath it via the parent popup's own `data-nested-dialog-open`
          // overlay (see the `after:` classes below), not a second backdrop.
          className={clstx(
            'fixed inset-0 z-modal-backdrop-1 bg-gray-900/30',
            'transition-opacity duration-300 data-starting-style:opacity-0 data-ending-style:opacity-0'
          )}
        />
        <Dialog.Popup
          ref={drag.ref}
          onPointerDown={drag.onPointerDown}
          data-testid="modal-core-panel"
          className={(state) =>
            clstx(
              'fixed',
              // Phone: a bottom sheet
              // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- No utility for calc() or min() sizing; iOS safe-area inset
              'max-sm:inset-x-0 max-sm:bottom-0 max-sm:max-h-[calc(100%-var(--spacing-12))] max-sm:pb-[env(safe-area-inset-bottom)]',
              // From `sm`, a centred dialog
              'sm:left-1/2 sm:top-1/2',
              // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Follows the drag offset set by the modal
              'sm:[translate:calc(-50%_+_var(--modal-drag-x,0px))_calc(-50%_+_var(--modal-drag-y,0px))]',
              // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Limits the transition to these properties
              'transition-[scale,opacity] duration-300 data-starting-style:scale-95 data-starting-style:opacity-0 data-ending-style:scale-95 data-ending-style:opacity-0',
              // Modal sizes, From `sm` fills screen upto max-w-*
              size && 'sm:w-full',
              size === 'sm' && 'sm:max-w-2xl',
              size === 'md' && 'sm:max-w-4xl',
              size === 'lg' && 'sm:max-w-6xl',
              size === 'xl' && 'sm:max-w-screen-xl',
              // Base UI sets `data-nested` on a popup rendered inside another
              // dialog, so a nested modal claims the higher band automatically.
              state.nested ? 'z-modal2' : 'z-modal1',
              // Recede behind a child: only while this modal has a nested dialog
              // open does Base UI set `data-nested-dialog-open`, which renders a
              // dimming overlay over this popup (and blocks its input) while the
              // child sits on top
              // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Inherits the parent's radius
              'after:absolute after:inset-0 after:z-sticky after:rounded-[inherit] after:bg-gray-900/30 after:pointer-events-auto after:content-none',
              // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Turns the overlay back on over after:content-none
              "data-nested-dialog-open:after:content-['']",
              className
            )
          }
          {...panelProps}
        >
          {children}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ModalCore;
